import React, { useMemo, useEffect } from 'react'
import { Form, Input, InputNumber, TimePicker, Button, Space, Spin, Alert, Row, Col, Select, Divider, Card, Tag } from 'antd'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import * as Types from '@/types'

dayjs.extend(duration)

interface ActivityEntryFormProps {
  operationId: string
  reportId: string
  activityCodes: Types.ActivityCode[]
  toolstrings?: Types.Toolstring[]
  templates?: Types.ActivityTemplate[]
  initialValues?: Partial<Types.OperationActivity>
  onSubmit: (values: Types.OperationActivityCreateRequest | Types.OperationActivityUpdateRequest) => Promise<void>
  onCancel?: () => void
  loading?: boolean
  isEditing?: boolean
}

export const ActivityEntryForm: React.FC<ActivityEntryFormProps> = ({
  operationId,
  reportId,
  activityCodes = [],
  toolstrings = [],
  templates = [],
  initialValues,
  onSubmit,
  onCancel,
  loading = false,
  isEditing = false,
}) => {
  const [form] = Form.useForm()
  const [error, setError] = React.useState<string | null>(null)
  const [selectedActivityCode, setSelectedActivityCode] = React.useState<Types.ActivityCode | undefined>()
  const [selectedTemplate, setSelectedTemplate] = React.useState<Types.ActivityTemplate | undefined>()
  const [durationDisplay, setDurationDisplay] = React.useState<string>('')

  // Watch for activity code selection to show/hide conditional fields
  const watchActivityCodeId = Form.useWatch('activity_code_id', form)

  useEffect(() => {
    if (watchActivityCodeId) {
      const code = activityCodes.find((c) => c.id === watchActivityCodeId)
      setSelectedActivityCode(code)
      // Auto-populate category fields if not already set
      if (code && !form.getFieldValue('major_category')) {
        form.setFieldsValue({
          major_category: code.major_category,
          main_category: code.main_category,
          sub_activity: code.sub_activity,
        })
      }
    }
  }, [watchActivityCodeId, activityCodes, form])

  // Watch for time changes to calculate duration
  const watchStartTime = Form.useWatch('start_time', form)
  const watchEndTime = Form.useWatch('end_time', form)

  useEffect(() => {
    if (watchStartTime && watchEndTime) {
      const start = dayjs(watchStartTime)
      const end = dayjs(watchEndTime)
      if (end.isAfter(start)) {
        const diff = dayjs.duration(end.diff(start))
        const hours = diff.asHours().toFixed(2)
        setDurationDisplay(`${hours} hrs`)
      } else {
        setDurationDisplay('Invalid time range')
      }
    }
  }, [watchStartTime, watchEndTime])

  const handleSubmit = async (values: any) => {
    try {
      setError(null)

      // Validate time range
      if (values.start_time && values.end_time) {
        const start = dayjs(values.start_time)
        const end = dayjs(values.end_time)
        if (!end.isAfter(start)) {
          setError('End time must be after start time')
          return
        }
      }

      const payload: Types.OperationActivityCreateRequest = {
        activity_code_id: values.activity_code_id || undefined,
        toolstring_id: values.toolstring_id || undefined,
        start_time: values.start_time?.toISOString() || '',
        end_time: values.end_time?.toISOString() || '',
        major_category: values.major_category,
        main_category: values.main_category,
        sub_activity: values.sub_activity,
        custom_activity_name: values.custom_activity_name,
        depth: values.depth || undefined,
        depth_unit: values.depth_unit || 'meters',
        fluid_level: values.fluid_level || undefined,
        weight: values.weight || undefined,
        pressure: values.pressure || undefined,
        structured_commentary: values.structured_commentary || undefined,
        free_text_commentary: values.free_text_commentary,
      }

      await onSubmit(payload)
      form.resetFields()
    } catch (err: any) {
      setError(err.message || 'Failed to save activity')
    }
  }

  // Group activity codes by major category for better organization
  const groupedActivityCodes = useMemo(() => {
    const grouped: Record<string, Types.ActivityCode[]> = {}
    activityCodes.forEach((code) => {
      if (!grouped[code.major_category]) {
        grouped[code.major_category] = []
      }
      grouped[code.major_category].push(code)
    })
    return grouped
  }, [activityCodes])

  // Generate activity code options with grouping
  const activityCodeOptions = useMemo(() => {
    const options: any[] = []
    Object.entries(groupedActivityCodes).forEach(([majorCategory, codes]) => {
      options.push({
        label: majorCategory,
        options: codes.map((code) => ({
          label: `${code.code} - ${code.display_name}`,
          value: code.id,
        })),
      })
    })
    return options
  }, [groupedActivityCodes])

  const toolstringOptions = useMemo(
    () =>
      toolstrings?.map((ts) => ({
        label: ts.name,
        value: ts.id,
      })) || [],
    [toolstrings]
  )

  const getMajorCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      STDBY: 'blue',
      HSE: 'green',
      NPT: 'red',
      Operation: 'orange',
    }
    return colors[category] || 'default'
  }

  const handleTemplateSelection = (templateId: string) => {
    const template = templates?.find((t) => t.id === templateId)
    if (template) {
      setSelectedTemplate(template)
    }
  }

  const handlePrefillFromTemplate = () => {
    if (!selectedTemplate) {
      setError('Please select a template first')
      return
    }

    // Pre-fill form with template data
    const templateData = {
      major_category: selectedTemplate.major_category || form.getFieldValue('major_category'),
      main_category: selectedTemplate.main_category || form.getFieldValue('main_category'),
      sub_activity: selectedTemplate.sub_activity || form.getFieldValue('sub_activity'),
    }

    // If template has duration, set it
    if (selectedTemplate.duration_minutes) {
      const durationMins = parseInt(selectedTemplate.duration_minutes)
      const startTime = form.getFieldValue('start_time')
      if (startTime) {
        const endTime = dayjs(startTime).add(durationMins, 'minute')
        templateData.end_time = endTime
      }
    }

    form.setFieldsValue(templateData)
    setError(null)
  }

  // Get available templates for selected activity code
  const availableTemplates = useMemo(() => {
    if (!selectedActivityCode?.default_template_id) {
      return templates || []
    }
    // If activity code has a default template, highlight it
    return templates || []
  }, [selectedActivityCode, templates])

  const templateOptions = useMemo(
    () =>
      availableTemplates.map((t) => ({
        label: `${t.name}${
          selectedActivityCode?.default_template_id === t.id ? ' (Default)' : ''
        }`,
        value: t.id,
      })) || [],
    [availableTemplates, selectedActivityCode]
  )

  return (
    <div style={{ padding: '20px 0' }}>
      {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} closable />}

      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          disabled={loading}
          initialValues={initialValues}
        >
          {/* Activity Selection Section */}
          <Card title="Activity Selection" size="small" style={{ marginBottom: '16px' }}>
            <Form.Item
              name="activity_code_id"
              label="Select Activity Code (or create custom)"
              tooltip="Choose from predefined activity codes or leave blank to enter custom activity"
            >
              <Select
                placeholder="Select or leave blank for custom activity"
                allowClear
                optionLabelProp="label"
                options={activityCodeOptions}
              />
            </Form.Item>

            {!watchActivityCodeId && (
              <Form.Item
                name="custom_activity_name"
                label="Custom Activity Name"
                rules={[{ required: !watchActivityCodeId, message: 'Enter custom activity name if no code selected' }]}
              >
                <Input placeholder="e.g., Equipment calibration, Well testing" />
              </Form.Item>
            )}

            {selectedActivityCode && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Category: </strong>
                  <Tag color={getMajorCategoryColor(selectedActivityCode.major_category)}>
                    {selectedActivityCode.major_category}
                  </Tag>
                </div>
                {selectedActivityCode.main_category && (
                  <div style={{ marginBottom: '8px' }}>
                    <strong>Main Category: </strong>
                    {selectedActivityCode.main_category}
                  </div>
                )}
                {selectedActivityCode.description && (
                  <div>
                    <strong>Description: </strong>
                    {selectedActivityCode.description}
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Template Selection Section */}
          {selectedActivityCode && availableTemplates.length > 0 && (
            <>
              <Divider />
              <Card title="Activity Templates (Optional)" size="small" style={{ marginBottom: '16px' }}>
                <Form.Item
                  label="Select Template to Pre-fill Form"
                  tooltip="Choose a template to automatically populate activity details"
                >
                  <Space style={{ width: '100%' }}>
                    <Select
                      style={{ flex: 1 }}
                      placeholder="Select a template..."
                      allowClear
                      options={templateOptions}
                      onChange={handleTemplateSelection}
                    />
                    <Button
                      type="dashed"
                      onClick={handlePrefillFromTemplate}
                      disabled={!selectedTemplate}
                    >
                      Pre-fill from Template
                    </Button>
                  </Space>
                </Form.Item>

                {selectedTemplate && (
                  <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '4px' }}>
                    <div style={{ marginBottom: '8px' }}>
                      <strong>Template: {selectedTemplate.name}</strong>
                    </div>
                    {selectedTemplate.description && (
                      <div style={{ marginBottom: '8px', fontSize: '12px' }}>
                        {selectedTemplate.description}
                      </div>
                    )}
                    {selectedTemplate.duration_minutes && (
                      <div style={{ marginBottom: '8px', fontSize: '12px' }}>
                        <strong>Expected Duration:</strong> {selectedTemplate.duration_minutes} minutes
                      </div>
                    )}
                    {selectedTemplate.required_tools && Array.isArray(selectedTemplate.required_tools) && (
                      <div style={{ fontSize: '12px' }}>
                        <strong>Required Tools:</strong> {selectedTemplate.required_tools.join(', ')}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </>
          )}

          <Divider />

          {/* Time Tracking Section */}
          <Card title="Time Tracking" size="small" style={{ marginBottom: '16px' }}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="start_time"
                  label="Start Time"
                  rules={[{ required: true, message: 'Please select start time' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="end_time"
                  label="End Time"
                  rules={[{ required: true, message: 'Please select end time' }]}
                >
                  <TimePicker format="HH:mm" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            {durationDisplay && (
              <div style={{ padding: '8px 12px', backgroundColor: '#e6f7ff', borderRadius: '4px', marginBottom: '16px' }}>
                <strong>Duration: {durationDisplay}</strong>
              </div>
            )}
          </Card>

          <Divider />

          {/* Activity Classification */}
          <Card title="Classification" size="small" style={{ marginBottom: '16px' }}>
            <Form.Item
              name="major_category"
              label="Major Category"
              rules={[{ required: true, message: 'Please select major category' }]}
            >
              <Select
                placeholder="STDBY, HSE, NPT, or Operation"
                options={[
                  { label: 'Standby (STDBY)', value: 'STDBY' },
                  { label: 'Health, Safety & Environment (HSE)', value: 'HSE' },
                  { label: 'Non-Productive Time (NPT)', value: 'NPT' },
                  { label: 'Operation', value: 'Operation' },
                ]}
              />
            </Form.Item>

            <Form.Item name="main_category" label="Main Category (Optional)">
              <Input placeholder="e.g., Wireline Unconventional, Slickline" />
            </Form.Item>

            <Form.Item name="sub_activity" label="Sub-Activity (Optional)">
              <Input placeholder="e.g., Logging Run, Perforation" />
            </Form.Item>
          </Card>

          <Divider />

          {/* Toolstring Selection */}
          <Card title="Toolstring (Optional)" size="small" style={{ marginBottom: '16px' }}>
            <Form.Item name="toolstring_id" label="Select Toolstring Used">
              <Select
                placeholder="Select toolstring if applicable"
                allowClear
                options={toolstringOptions}
              />
            </Form.Item>
          </Card>

          <Divider />

          {/* Conditional Measurements Section */}
          {(selectedActivityCode?.track_depth || selectedActivityCode?.track_fluid_level ||
            selectedActivityCode?.track_weight || selectedActivityCode?.track_pressure) && (
            <Card title="Measurements" size="small" style={{ marginBottom: '16px' }}>
              {selectedActivityCode?.track_depth && (
                <Row gutter={16}>
                  <Col xs={24} sm={16}>
                    <Form.Item name="depth" label="Depth">
                      <InputNumber placeholder="0" step={1} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8}>
                    <Form.Item name="depth_unit" label="Unit" initialValue="meters">
                      <Select
                        options={[
                          { label: 'Meters', value: 'meters' },
                          { label: 'Feet', value: 'feet' },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {selectedActivityCode?.track_fluid_level && (
                <Form.Item name="fluid_level" label="Fluid Level">
                  <InputNumber placeholder="0" step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              )}

              {selectedActivityCode?.track_weight && (
                <Form.Item name="weight" label="Weight on Tools">
                  <InputNumber placeholder="0" step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              )}

              {selectedActivityCode?.track_pressure && (
                <Form.Item name="pressure" label="Pressure (bar)">
                  <InputNumber placeholder="0" step={0.1} style={{ width: '100%' }} />
                </Form.Item>
              )}
            </Card>
          )}

          <Divider />

          {/* Commentary Section */}
          <Card title="Commentary" size="small" style={{ marginBottom: '16px' }}>
            {selectedActivityCode?.commentary_template?.placeholders && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f0f5ff', borderRadius: '4px' }}>
                <strong style={{ display: 'block', marginBottom: '8px' }}>Expected Data Points:</strong>
                <div>
                  {selectedActivityCode.commentary_template.placeholders.map((placeholder, idx) => (
                    <Tag key={idx} color="blue">
                      {placeholder}
                    </Tag>
                  ))}
                </div>
              </div>
            )}

            <Form.Item
              name="free_text_commentary"
              label="Commentary"
              rules={[{ required: true, message: 'Please enter commentary' }]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Detailed notes about this activity, observations, issues encountered, etc."
              />
            </Form.Item>
          </Card>

          {/* Submit Section */}
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={loading}>
                {isEditing ? 'Update Activity' : 'Add Activity'}
              </Button>
              {onCancel && <Button onClick={onCancel}>Cancel</Button>}
            </Space>
          </Form.Item>
        </Form>
      </Spin>
    </div>
  )
}

export default ActivityEntryForm
